import { QueryClientProvider, type QueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { AuthProvider } from '@/auth/auth-context';
import { ThemeProvider } from '@/components/layout/theme';
import { TooltipProvider } from '@/components/ui/primitives';
import { createQueryClient } from '@/lib/query-client';
import { WebScopeProvider } from '@/lib/scope/web-scope';

/**
 * Every provider the app needs, in one place.
 *
 * Exported separately from `main.tsx` so tests can mount exactly the same provider stack around a
 * single page. A test that wraps its component in a *different* set of providers is testing a
 * different application than the one that ships.
 */
export function AppProviders({
  children,
  queryClient,
}: {
  /** The tree to wrap. */
  children: ReactNode;
  /** Inject a client with retries disabled in tests. */
  queryClient?: QueryClient | undefined;
}): ReactNode {
  const [client] = useState(() => queryClient ?? createQueryClient());
  return (
    <QueryClientProvider client={client}>
      <ThemeProvider>
        <TooltipProvider>
          <AuthProvider>
            <WebScopeProvider>{children}</WebScopeProvider>
          </AuthProvider>
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
